import app from '../../forum/app';
import { throttle } from 'throttle-debounce';
import anchorScroll from '../../common/utils/anchorScroll';
import type Discussion from '../../common/models/Discussion';
import type Post from '../../common/models/Post';

export default class PostStreamState {
  /**
   * The number of posts to load per page.
   */
  static loadCount = 20;

  /**
   * The discussion to display the post stream for.
   */
  discussion: Discussion;

  orderedPostIds: Array<string | null> = [];

  totalCount = 0;

  pinFirstPost = false;

  private firstPostId(): string | null {
    const first = this.discussion.firstPost() as unknown;
    if (!first || typeof first === 'boolean') return null;
    return (first as Post).id() ?? null;
  }

  /**
   * Whether or not the infinite-scrolling auto-load functionality is
   * disabled.
   */
  paused = false;

  loadPageTimeouts: Record<number, NodeJS.Timeout> = {};
  pagesLoading = 0;

  index = 0;
  number = 1;
  near = 0;
  /**
   * The number of posts that are currently visible in the viewport.
   */
  visible = 1;
  visibleStart = 0;
  visibleEnd = 0;

  animateScroll = false;
  needsScroll = false;
  targetPost: { number: number } | { index: number; reply?: boolean } | null = null;

  /**
   * The description to render on the scrubber.
   */
  description = '';

  /**
   * When the page is scrolled, goToIndex is called, or the page is loaded,
   * various listeners result in the scrubber being updated with a new
   * position and values. However, if goToNumber is called, the scrubber
   * will not be updated. Accordingly, we add logic to the scrubber's
   * onupdate to update itself, but only when needed, as indicated by this
   * property.
   *
   */
  forceUpdateScrubber = false;

  loadPromise: Promise<void> | null = null;

  loadNext: () => void;
  loadPrevious: () => void;

  constructor(
    discussion: Discussion,
    includedPosts: Post[] = [],
    near: number = 0,
    options: { pinFirstPost?: boolean; initOrderFromDiscussion?: boolean } = {}
  ) {
    this.discussion = discussion;
    this.near = near;
    this.pinFirstPost = !!options.pinFirstPost;
    this.loadNext = throttle(300, this._loadNext);
    this.loadPrevious = throttle(300, this._loadPrevious);

    const initOrderFromDiscussion = options.initOrderFromDiscussion !== false;
    this.initOrderedPostIds(initOrderFromDiscussion);
    this.show(includedPosts);
  }

  private initOrderedPostIds(initOrderFromDiscussion: boolean) {
    // 不再依赖后端返回的总数，改为按需增量加载
    this.totalCount = 0;
    this.orderedPostIds = [];

    if (this.pinFirstPost) {
      const firstId = this.firstPostId();
      if (firstId) {
        this.orderedPostIds[0] = firstId;
        this.totalCount = 1;
      }
    }
  }

  /**
   * 直接插入新帖子到流中，无需重新加载
   * 新帖子将被插入到列表的开头（如果pinFirstPost为真，则在第一个帖子之后）。
   */
  insertPost(post: Post): number {
    const postId = post.id();
    if (!postId) return -1;

    // 更新 totalCount：仅作为当前已加载数量的计数，不再作为上限使用
    const currentCount = this.orderedPostIds.length;
    this.totalCount = currentCount + 1;

    // 确保 orderedPostIds 长度足够
    if (this.orderedPostIds.length < this.totalCount) {
      const oldLength = this.orderedPostIds.length;
      this.orderedPostIds.length = this.totalCount;
      // 填充新扩展的部分为 null
      for (let i = oldLength; i < this.totalCount; i++) {
        this.orderedPostIds[i] = null;
      }
    }

    // 将新帖子插入到开头位置
    // 如果 pinFirstPost 为 true，插入到索引1（第一个帖子后面）
    // 否则插入到索引0
    const insertIndex = this.pinFirstPost ? 1 : 0;

    // 将现有帖子向后移动一位
    // 从后往前移动，避免覆盖数据
    for (let i = this.totalCount - 1; i > insertIndex; i--) {
      this.orderedPostIds[i] = this.orderedPostIds[i - 1];
    }

    // 插入新帖子
    this.orderedPostIds[insertIndex] = postId;

    // 更新 discussion 的 posts 关系，将新帖子添加到关系列表中
    const discussionAny = this.discussion as any;
    const links: { id: string; type: string }[] =
      (discussionAny.rawRelationship && discussionAny.rawRelationship('posts')) || discussionAny.data?.relationships?.posts?.data || [];

    if (!links.find((link) => link.id === postId)) {
      links.push({ id: postId, type: 'posts' });
    }
    if (this.visibleEnd === 0) {
      this.visibleEnd = Math.min(this.visibleStart + this.totalCount, this.orderedPostIds.length);
    }
    this.visibleEnd += 1;

    return insertIndex;
  }

  /**
   * Update the stream so that it loads and includes the latest posts in the
   * discussion, if the end is being viewed.
   */
  update() {
    if (!this.viewingEnd()) return Promise.resolve();

    // 不再依赖后端统计的总数，这里仅以当前已加载的数量为准
    this.totalCount = this.orderedPostIds.length;

    this.visibleEnd = this.count();

    // 重新加载可见范围，确保顺序正确
    return this.loadRange(this.visibleStart, this.visibleEnd);
  }

  /**
   * Load and scroll up to the first post in the discussion.
   */
  goToFirst(): Promise<void> {
    return this.goToIndex(0);
  }

  /**
   * Load and scroll down to the last post in the discussion.
   */
  goToLast(): Promise<void> {
    return this.goToIndex(this.count() - 1, true);
  }

  /**
   * Load and scroll to a post with a certain number.
   *
   * @param number The post number to go to. If 'reply', go to the last post and scroll the reply preview into view.
   */
  goToNumber(number: number | 'reply', noAnimation = false): Promise<void> {
    // If we want to go to the reply preview, then we will go to the end of the
    // discussion and then scroll to the very bottom of the page.
    console.log('goToNumber', number);

    if (number === 'reply') {
      const resultPromise = this.goToLast();
      this.targetPost = { ...(this.targetPost as { index: number }), reply: true };
      return resultPromise;
    }
    const cachedNear = (app.cache as any).discussionNear?.[`discussionPage${this.discussion.id()}`];
    if (cachedNear) {
      this.visibleStart = Math.max(0, cachedNear.visibleStart - 1);
      this.visibleEnd = cachedNear.visibleEnd;
    }

    this.paused = true;

    this.loadPromise = this.loadNearNumber(number);

    this.needsScroll = true;
    this.targetPost = { number };
    this.animateScroll = !noAnimation;
    this.number = number;

    // In this case, the redraw is only called after the response has been loaded
    // because we need to know the indices of the post range before we can
    // start scrolling to items. Calling redraw early causes issues.
    // Since this is only used for external navigation to the post stream, the delay
    // before the stream is moved is not an issue.
    return this.loadPromise.then(() => m.redraw());
  }

  /**
   * Load and scroll to a certain index within the discussion.
   */
  goToIndex(index: number, noAnimation = false): Promise<void> {
    this.paused = true;

    this.loadPromise = this.loadNearIndex(index);

    this.needsScroll = true;
    this.targetPost = { index };
    this.animateScroll = !noAnimation;
    this.index = index;

    m.redraw();

    return this.loadPromise;
  }

  /**
   * Clear the stream and load posts near a certain number. Returns a promise.
   * If the post with the given number is already loaded, the promise will be
   * resolved immediately.
   */
  loadNearNumber(number: number): Promise<void> {
    if (this.discussionPosts().some((post) => post && Number(post.number()) === Number(number))) {
      this.rangeFunction(this.discussionPosts(), this.visibleStart, this.visibleEnd, PostStreamState.loadCount);
      const cachedNear = (app.cache as any).discussionNear?.[`discussionPage${this.discussion.id()}`];
      if (cachedNear) {
        delete (app.cache as any).discussionNear[`discussionPage${this.discussion.id()}`];
      }
      return Promise.resolve();
    }

    this.reset();
    const cachedNear = (app.cache as any).discussionNear?.[`discussionPage${this.discussion.id()}`];
    if (cachedNear) {
      number = parseInt(cachedNear);
      this.visibleStart = Math.max(0, cachedNear.visibleStart - 1);
      this.visibleEnd = cachedNear.visibleEnd;
      return this.loadRange(this.visibleStart, this.visibleEnd).then(() => {
        delete (app.cache as any).discussionNear[`discussionPage${this.discussion.id()}`];
        this.show.bind(this);
      });
    }
    // return app.store
    //   .find<Post[]>('posts', {
    //     filter: { discussion: this.discussion.id() as string},
    //     page: { near: number },
    //   })
    //   .then(()=>{
    //     this.show.bind(this)
    //   });
    const index = Math.max(0, (Number(number) || 1) - 1);
    return this.loadNearIndex(index).then(() => undefined);
  }
  requestParams(start: number, end: number): Record<string, unknown> {
    const currentSort = (app.current.get('discussionSort') as string) || m.route.param('sort') || 'top';
    const cachedNear = (app.cache as any).discussionNear?.[`discussionPage${this.discussion.id()}`];
    if (cachedNear) {
      const limit = this.visibleEnd - this.visibleStart;
      return {
        filter: {
          discussion: this.discussion.id() + '',
          type: 'comment',
        },
        page: { offset: this.visibleStart++, limit },
        sort: this.sortMap()[cachedNear.sort],
      };
    } else {
      const apiOffset = this.pinFirstPost ? (start === 0 ? 0 : Math.max(0, start - 1)) : start;
      return {
        filter: {
          discussion: this.discussion.id() + '',
          type: 'comment',
        },
        page: { offset: apiOffset, limit: PostStreamState.loadCount },
        sort: this.sortMap()[currentSort],
      };
    }
    // return {
    //   bySlug: true,
    //   page: { near: this.near },
    //   sort: this.sortMap()[currentSort],
    // };
  }

  sortMap() {
    const map: any = {};

    map.latest = 'latest';
    map.top = 'top';
    map.oldest = 'oldest';
    map.op = 'op';
    map.myReply = 'myReply';

    return map;
  }
  /**
   * Clear the stream and load posts near a certain index. A page of posts
   * surrounding the given index will be loaded. Returns a promise. If the given
   * index is already loaded, the promise will be resolved immediately.
   */
  loadNearIndex(index: number): Promise<void> {
    if (index >= this.visibleStart && index < this.visibleEnd) {
      return Promise.resolve();
    }

    const start = Math.max(0, index - Math.floor(PostStreamState.loadCount / 2));
    const end = start + PostStreamState.loadCount;

    this.reset(start, end);

    return this.loadRange(start, end).then(this.show.bind(this));
  }

  /**
   * Load the next page of posts.
   */
  _loadNext() {
    const start = this.visibleEnd;
    // 不再使用 count() 限制可见区的上限，让新加载的数据可以扩展可见范围
    const end = (this.visibleEnd = this.visibleEnd + PostStreamState.loadCount);

    // Unload the posts which are two pages back from the page we're currently
    // loading.
    const twoPagesAway = start - PostStreamState.loadCount;
    if (twoPagesAway > this.visibleStart && twoPagesAway >= 0) {
      this.visibleStart = twoPagesAway + 1;

      if (this.loadPageTimeouts[twoPagesAway]) {
        clearTimeout(this.loadPageTimeouts[twoPagesAway]);
        delete this.loadPageTimeouts[twoPagesAway];
        this.pagesLoading--;
      }
    }

    this.loadPage(start, end);
  }

  /**
   * Load the previous page of posts.
   */
  _loadPrevious() {
    const end = this.visibleStart;
    const start = (this.visibleStart = this.sanitizeIndex(this.visibleStart - PostStreamState.loadCount - 1));

    // Unload the posts which are two pages back from the page we're currently
    // loading.
    const twoPagesAway = start + PostStreamState.loadCount * 2;
    if (twoPagesAway < this.visibleEnd && twoPagesAway <= this.count()) {
      this.visibleEnd = twoPagesAway;
      (this as any).noMore = false;
      if (this.loadPageTimeouts[twoPagesAway]) {
        clearTimeout(this.loadPageTimeouts[twoPagesAway]);
        delete this.loadPageTimeouts[twoPagesAway];
        this.pagesLoading--;
      }
    }

    this.loadPage(start, end, true);
  }

  /**
   * Load a page of posts into the stream and redraw.
   */
  loadPage(start: number, end: number, backwards = false) {
    this.pagesLoading++;

    const redraw = () => {
      if (start < this.visibleStart || end > this.visibleEnd) return;

      const anchorIndex = backwards ? Math.min(this.visibleEnd - 1, this.count()) : this.visibleStart;
      anchorScroll(`.PostStream-item[data-index="${anchorIndex}"]`, m.redraw.sync);
    };
    redraw();

    this.loadPageTimeouts[start] = setTimeout(
      () => {
        this.loadRange(start, end).then(() => {
          redraw();
          this.pagesLoading--;
        });
        delete this.loadPageTimeouts[start];
      },
      this.pagesLoading - 1 ? 1000 : 0
    );
  }

  /**
   * Load and inject the specified range of posts into the stream, without
   * clearing it.
   */
  loadRange(start: number, end: number): Promise<Post[]> {
    let limit = PostStreamState.loadCount;
    const cachedNear = (app.cache as any).discussionNear?.[`discussionPage${this.discussion.id()}`];
    if (cachedNear) {
      limit = Math.max(limit, this.visibleEnd - this.visibleStart);
    }

    // this.discussion
    //   .postIds()
    //   .slice(start, end)
    //   .forEach((id) => {
    //     const post = app.store.getById<Post>('posts', id);

    //     if (post && post.discussion() && typeof post.canEdit() !== 'undefined') {
    //       loaded.push(post);
    //     } else {
    //       loadIds.push(id);
    //     }
    //   });

    // const apiOffset = this.pinFirstPost ? (start === 0 ? 0 : Math.max(0, start - 1)) : start;
    return app.store.find<Post[]>('posts', this.requestParams(start, end)).then((newPosts) => {
      this.rangeFunction(newPosts, start, end, limit);

      return newPosts;
    });
  }
  rangeFunction(newPosts: any, start: number, end: number, limit: number) {
    const displayNeeded = Math.max(0, end - start);
    const firstId = this.pinFirstPost ? this.firstPostId() : null;

    const discussionAny = this.discussion as any;

    const links: { id: string; type: string }[] =
      (discussionAny.rawRelationship && discussionAny.rawRelationship('posts')) || discussionAny.data?.relationships?.posts?.data || [];

    newPosts.forEach((post: any) => {
      const id = post.id();
      if (!id) return;

      if (!links.find((link) => link.id === id)) {
        links.push({ id, type: 'posts' });
      }
    });

    const apiIds = newPosts.map((p: any) => p.id()).filter(Boolean) as string[];
    const filteredIds = firstId ? apiIds.filter((id) => id !== firstId) : apiIds;

    let insertIds: string[] = [];
    if (this.pinFirstPost) {
      if (start === 0) {
        insertIds = [];
        if (firstId) insertIds.push(firstId);
        insertIds.push(...filteredIds.slice(0, Math.max(0, displayNeeded - insertIds.length)));
      } else {
        insertIds = filteredIds.slice(0, displayNeeded);
      }
      if (firstId) this.orderedPostIds[0] = firstId;
    } else {
      insertIds = filteredIds.slice(0, displayNeeded || limit);
    }

    // 在插入新帖子ID之前，先检查是否需要清空范围内的旧数据
    // 如果是在 update() 调用的情况下（即查看末尾时），应该重新填充整个可见范围
    // 通过检查 end 是否等于 count() 来判断是否是更新操作
    const isUpdating = this.viewingEnd() && end >= this.count() - 1;

    if (isUpdating && start < this.orderedPostIds.length) {
      // 清空可见范围内的旧数据，避免顺序混乱
      // 只清空从 start 到 Math.min(end, this.orderedPostIds.length) 的范围
      const clearEnd = Math.min(end, this.orderedPostIds.length);
      for (let i = start; i < clearEnd; i++) {
        this.orderedPostIds[i] = null;
      }
    }

    for (let i = 0; i < insertIds.length; i++) {
      const idx = start + i;
      if (idx < 0) continue;
      if (idx >= this.orderedPostIds.length) {
        this.orderedPostIds.length = idx + 1;
      }
      // this.orderedPostIds[idx] = insertIds[i];
      // 如果是更新操作，直接覆盖；否则只有在位置为空时才覆盖
      if (isUpdating || !this.orderedPostIds[idx]) {
        this.orderedPostIds[idx] = insertIds[i];
      }
    }

    // 更新当前已加载的总数，供 count() 和 viewingEnd 使用
    this.totalCount = Math.max(this.totalCount, this.orderedPostIds.length);
    if (this.visibleEnd === 0) {
      this.visibleEnd = Math.min(this.visibleStart + this.totalCount, this.orderedPostIds.length);
    }
    if (newPosts.length < limit) {
      // 返回条数 < limit，可以认为已经没有更多了
      (this as any).noMore = true;
    }
  }
  /**
   * Set up the stream with the given array of posts.
   */
  show(posts: Post[]) {
    if (!posts.length) return;

    const firstId = this.pinFirstPost ? this.firstPostId() : null;
    const ids = posts
      .map((p) => p.id())
      .filter(Boolean)
      .filter((id) => (firstId ? id !== firstId : true)) as string[];

    if (this.pinFirstPost && firstId) this.orderedPostIds[0] = firstId;

    const insertBase = this.visibleStart;
    const insertAt = this.pinFirstPost && insertBase === 0 ? 1 : insertBase;
    const maxInsert = Math.min(ids.length, Math.max(0, this.orderedPostIds.length - insertAt));

    for (let i = 0; i < maxInsert; i++) {
      this.orderedPostIds[insertAt + i] = ids[i];
    }
  }

  /**
   * Reset the stream so that a specific range of posts is displayed. If a range
   * is not specified, the first page of posts will be displayed.
   */
  reset(start?: number, end?: number) {
    this.visibleStart = start || 0;
    this.visibleEnd = this.sanitizeIndex(end || PostStreamState.loadCount) === 1 ? 0 : this.sanitizeIndex(end || PostStreamState.loadCount);
  }

  /**
   * Get the visible page of posts.
   */
  posts(): (Post | null)[] {
    return this.orderedPostIds.slice(this.visibleStart, this.visibleEnd).map((id) => {
      if (!id) return null;
      const post = app.store.getById<Post>('posts', id);
      return post && post.discussion() && typeof post.canEdit() !== 'undefined' ? post : null;
    });
  }

  discussionPosts(): (Post | null)[] {
    return this.discussion
      .postIds()
      .slice(this.visibleStart, Math.max(this.visibleEnd, PostStreamState.loadCount))
      .map((id) => {
        if (!id) return null;
        const post = app.store.getById<Post>('posts', id);
        return post && post.discussion() && typeof post.canEdit() !== 'undefined' ? post : null;
      });
  }
  /**
   * Get the total number of posts in the discussion.
   */
  count(): number {
    // 不再依赖后端返回的总数，始终以当前已加载的数量为准
    return this.orderedPostIds.length;
  }

  /**
   * Check whether or not the scrubber should be disabled, i.e. if all of the
   * posts are visible in the viewport.
   */
  disabled(): boolean {
    return this.visible >= this.count();
  }

  /**
   * Are we currently viewing the end of the discussion?
   */
  viewingEnd(): boolean {
    // In some cases, such as if we've stickied a post, an event post
    // may have been added / removed. This means that `this.visibleEnd`
    // and`this.count()` will be out of sync by 1 post, but we are still
    // "viewing the end" of the post stream, so we should still reload
    // all posts up until the last one.

    return Math.abs(this.count() - this.visibleEnd) <= 1;
  }

  /**
   * Make sure that the given index is not outside of the possible range of
   * indexes in the discussion.
   */
  sanitizeIndex(index: number) {
    return Math.max(0, Math.min(this.count(), Math.floor(index)));
  }
}
